import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileUp, ShieldAlert, ShieldX, ShieldQuestion, ChevronDown, ScanLine } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 100;

const VERDICTS = {
  dangerous: { label: "DANGEROUS", Icon: ShieldX, text: "text-[#FF3366]", border: "border-[#FF3366]/50", bg: "bg-[#FF3366]/5" },
  suspicious: { label: "SUSPICIOUS", Icon: ShieldAlert, text: "text-[#FACC15]", border: "border-[#FACC15]/40", bg: "bg-[#FACC15]/5" },
  no_warnings: { label: "NO OBVIOUS WARNING SIGNS", Icon: ShieldQuestion, text: "text-[#22C55E]", border: "border-[#22C55E]/40", bg: "bg-[#22C55E]/5" },
};

export default function ApkChecker() {
  const [phase, setPhase] = useState("idle"); // idle | uploading | result
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [showPerms, setShowPerms] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setPhase("idle"); setResult(null); setError(""); setFileName(""); setShowPerms(false); if (inputRef.current) inputRef.current.value = ""; };

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setError("");
    if (!f.name.toLowerCase().endsWith(".apk")) { setError("Please choose a .apk file."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setError(`File is larger than the ${MAX_MB} MB limit.`); return; }
    setFileName(f.name);
    setPhase("uploading");
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await fetch(`${API}/api/apk/check`, { method: "POST", body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "The APK could not be analyzed. Please try again.");
      setResult(data);
      setPhase("result");
      setTimeout(() => document.querySelector('[data-testid="apk-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch (err) {
      setError(err.message && !err.message.includes("fetch") ? err.message : "Connection problem — please try again.");
      setPhase("idle");
    }
  };

  const v = result ? VERDICTS[result.verdict] || VERDICTS.suspicious : null;

  return (
    <>
      <Seo
        title="Free APK Checker — Inspect an Android App Before Installing | LinkExpln"
        description="Upload an APK and LinkExpln shows its fingerprint, package name, certificate, permissions and warning signs before you install it. Free, files never stored."
      />
      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">APK Checker</p>
          <h1 data-testid="apk-checker-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLines lines={["INSPECT AN APK", "BEFORE YOU INSTALL."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Upload an Android app file (.apk, up to {MAX_MB} MB) and we'll show its fingerprint,
            package identity, signing certificate, permissions and warning signs.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="border border-white/10 bg-[#0A121D] p-6 sm:p-10">
              {phase !== "result" && (
                <>
                  <input ref={inputRef} type="file" accept=".apk" onChange={handleFile} className="hidden" id="apk-file-input" data-testid="apk-file-input" />
                  <button
                    data-testid="apk-upload-button"
                    disabled={phase === "uploading"}
                    onClick={() => inputRef.current && inputRef.current.click()}
                    className="w-full border border-dashed border-[#00C5FE]/40 hover:border-[#00C5FE] bg-black/40 px-6 py-12 flex flex-col items-center gap-3 transition-colors duration-200 disabled:opacity-60"
                  >
                    <FileUp size={28} className="text-[#00C5FE]" />
                    <span className="font-heading font-bold text-white">
                      {phase === "uploading" ? `Analyzing ${fileName}…` : "Choose an APK file"}
                    </span>
                    <span className="font-mono-ls text-[10px] tracking-[0.15em] uppercase text-zinc-400">
                      {phase === "uploading" ? "This can take a moment for large files" : `.apk only · max ${MAX_MB} MB · file is analyzed and immediately deleted`}
                    </span>
                  </button>
                  {error && <p data-testid="apk-error" role="alert" className="mt-4 font-mono-ls text-xs text-[#FF3366]">{error}</p>}
                </>
              )}

              <AnimatePresence>
                {phase === "result" && result && v && (
                  <motion.div key="r" data-testid="apk-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`border ${v.border} ${v.bg}`}>
                    <div className="p-6 sm:p-8">
                      <div className="flex flex-wrap items-center gap-4">
                        <v.Icon size={34} className={`${v.text} shrink-0`} strokeWidth={2} />
                        <p data-testid="apk-verdict" className={`font-heading font-black tracking-tight text-xl sm:text-2xl ${v.text}`}>{v.label}</p>
                      </div>
                      <p data-testid="apk-explanation" className="mt-4 text-zinc-300 text-sm leading-relaxed">{result.explanation}</p>

                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[["Package", result.package || "—"], ["App name", result.app_name || "—"],
                          ["Version", `${result.version_name || "—"} (${result.version_code || "—"})`],
                          ["Size", `${(result.size_bytes / 1048576).toFixed(1)} MB`]].map(([k, val]) => (
                          <div key={k} className="border border-white/10 bg-black/40 px-4 py-3">
                            <p className="font-mono-ls text-[9px] tracking-[0.2em] uppercase text-zinc-500">{k}</p>
                            <p className="font-mono-ls text-xs text-white break-all mt-1">{val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 border border-white/10 bg-black/40 px-4 py-3">
                        <p className="font-mono-ls text-[9px] tracking-[0.2em] uppercase text-zinc-500">SHA-256 fingerprint</p>
                        <p data-testid="apk-sha256" className="font-mono-ls text-[11px] text-[#00C5FE] break-all mt-1">{result.sha256}</p>
                      </div>

                      {result.certificates && result.certificates.length > 0 ? (
                        <div className="mt-5">
                          <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Signing certificate</p>
                          {result.certificates.map((c, i) => (
                            <div key={i} className="border border-white/10 bg-black/40 px-4 py-3 text-xs text-zinc-300 space-y-1">
                              <p className="break-words"><span className="text-zinc-500">Signed by:</span> {c.subject}</p>
                              <p><span className="text-zinc-500">Valid:</span> {c.valid_from.slice(0, 10)} → {c.valid_to.slice(0, 10)}{c.self_signed ? " · self-signed (normal for Android)" : ""}</p>
                              <p className="font-mono-ls text-[10px] break-all"><span className="text-zinc-500">Cert SHA-256:</span> {c.sha256}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-5 font-mono-ls text-xs text-[#FF3366]">No readable signing certificate.</p>
                      )}

                      {result.warnings && result.warnings.length > 0 && (
                        <div data-testid="apk-warnings" className="mt-6">
                          <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Warnings</p>
                          <ul className="space-y-2">
                            {result.warnings.map((w, i) => (
                              <li key={i} className={`flex gap-2 text-sm leading-relaxed ${v.text}`}><span className="shrink-0 mt-0.5">⚠</span><span className="text-zinc-300">{w}</span></li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {result.permissions && result.permissions.length > 0 && (
                        <div className="mt-6 border-t border-white/10 pt-4">
                          <button type="button" data-testid="apk-perms-toggle" onClick={() => setShowPerms((s) => !s)}
                            className="inline-flex items-center gap-2 font-mono-ls text-[11px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition-colors duration-200">
                            <ChevronDown size={14} className={`transition-transform duration-200 ${showPerms ? "rotate-180" : ""}`} />
                            All permissions ({result.permissions.length})
                          </button>
                          {showPerms && (
                            <ul data-testid="apk-perms-list" className="mt-3 space-y-1 max-h-60 overflow-y-auto">
                              {result.permissions.map((p) => (
                                <li key={p} className={`font-mono-ls text-[11px] break-all ${result.flagged_permissions.some((f) => f.permission === p) ? "text-[#FACC15]" : "text-zinc-400"}`}>{p}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      <button data-testid="apk-reset-button" onClick={reset}
                        className="mt-7 font-mono-ls text-xs tracking-[0.15em] uppercase text-[#00C5FE] hover:text-white transition-colors duration-200 underline underline-offset-4">
                        Check another APK
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <p className="mt-8 font-mono-ls text-[10px] tracking-[0.1em] leading-relaxed text-zinc-500 uppercase">
                This is a static inspection tool, not antivirus. It cannot guarantee any APK is safe —
                even apps with no warning signs can be harmful. Uploaded files are analyzed in a temporary
                location and deleted immediately; only a minimal scan summary (fingerprint, package, verdict)
                is kept for up to 90 days. Only install apps from official stores and developers you trust.
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-12 border border-white/10 p-6 sm:p-8">
              <p className="ls-overline mb-4">Got a suspicious link instead?</p>
              <Link to="/scanner" data-testid="apk-to-scanner-link" className="inline-flex items-center gap-2 text-white hover:text-[#00C5FE] font-heading font-semibold text-sm transition-colors duration-200">
                <ScanLine size={15} /> Scan a URL with LinkExpln
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
